import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import api from '../utils/api';
import { Mail, Lock, Loader2, AlertTriangle, XCircle, KeyRound, CheckCircle, User } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null); // { checking, exists, isActivated, canAccess, name }
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const { login } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Vérifier le statut du compte quand on quitte le champ email
  const checkEmailStatus = async () => {
    if (!email.trim() || !isValidEmail(email)) {
      setEmailStatus(null);
      return;
    }

    setEmailStatus({ checking: true });

    try {
      const res = await api.post('/auth/check-email', { email });
      const data = res.data;

      if (!data.exists) {
        setEmailStatus({ exists: false });
      } else if (!data.canAccess) {
        setEmailStatus({ 
          exists: true, 
          canAccess: false, 
          message: data.message 
        });
        setError({
          type: 'error',
          message: 'Ce compte n\'a pas accès à ce portail. Vérifiez que vous utilisez le bon site.'
        });
      } else if (!data.isActivated) {
        setEmailStatus({ 
          exists: true, 
          canAccess: true, 
          isActivated: false,
          name: data.name
        });
        setError({
          type: 'info',
          message: `Bienvenue ${data.name || ''} ! Votre compte n'est pas encore activé.`,
          action: 'setup'
        });
      } else {
        setEmailStatus({ 
          exists: true, 
          canAccess: true, 
          isActivated: true,
          name: data.name
        });
        setError(null);
      }
    } catch (err) {
      console.error('Erreur check email:', err);
      setEmailStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError({ type: 'warning', message: 'Veuillez entrer votre adresse email' });
      return;
    }
    if (!isValidEmail(email)) {
      setError({ type: 'warning', message: 'Veuillez entrer une adresse email valide' });
      return;
    }
    if (!password) {
      setError({ type: 'warning', message: 'Veuillez entrer votre mot de passe' });
      return;
    }

    // Si le compte n'est pas activé, rediriger vers forgot-password
    if (emailStatus?.exists && !emailStatus?.isActivated) {
      setError({
        type: 'info',
        message: 'Votre compte n\'est pas encore activé. Définissez votre mot de passe pour continuer.',
        action: 'setup'
      });
      return;
    }

    // Si le compte n'a pas accès au tenant
    if (emailStatus?.exists && !emailStatus?.canAccess) {
      setError({
        type: 'error',
        message: 'Vous n\'êtes pas autorisé à vous connecter sur ce portail.'
      });
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Rediriger vers la page d'origine si disponible (ex : lien depuis un email)
        const returnTo = searchParams.get('returnTo');
        navigate(returnTo || '/tickets');
      } else {
        const errorMessage = result.error || 'Erreur de connexion';
        
        if (result.needsPasswordSetup) {
          setError({
            type: 'info',
            message: 'Votre compte n\'est pas encore activé.',
            action: 'setup'
          });
        } else if (errorMessage.includes('Identifiants invalides')) {
          setError({
            type: 'error',
            message: 'Mot de passe incorrect.'
          });
        } else if (errorMessage.includes('pas autorisé')) {
          setError({
            type: 'error',
            message: 'Vous n\'êtes pas autorisé à vous connecter sur ce portail.'
          });
        } else {
          setError({ type: 'error', message: errorMessage });
        }
      }
    } catch (err) {
      setError({
        type: 'error',
        message: 'Impossible de contacter le serveur. Vérifiez votre connexion internet.'
      });
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = tenant?.slug ? `/logos/${tenant.slug}.png` : null;
  const hasLogo = logoUrl && !logoError;
  
  const getEmailPlaceholder = () => {
    if (tenant?.supportEmail) return `email@${tenant.supportEmail.split('@')[1]}`;
    return 'vous@exemple.com';
  };

  const getErrorStyles = (type) => {
    switch (type) {
      case 'warning':
        return { container: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400', icon: AlertTriangle };
      case 'info':
        return { container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400', icon: KeyRound };
      default:
        return { container: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400', icon: XCircle };
    }
  };

  // Indicateur de statut email
  const renderEmailStatus = () => {
    if (!emailStatus) return null;

    if (emailStatus.checking) {
      return <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />;
    }

    if (emailStatus.exists && emailStatus.canAccess && emailStatus.isActivated) {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <CheckCircle className="w-5 h-5" />
        </div>
      );
    }

    if (emailStatus.exists && !emailStatus.isActivated) {
      return <KeyRound className="w-5 h-5 text-blue-500" />;
    }

    if (emailStatus.exists && !emailStatus.canAccess) {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="text-center mb-8">
            {hasLogo ? (
              <img src={logoUrl} alt={tenant?.name || 'Logo'} className="h-16 mx-auto mb-4 object-contain" onError={() => setLogoError(true)} />
            ) : (
              <>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}>
                  {tenant?.name?.charAt(0) || 'S'}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{tenant?.name || 'Support'}</h1>
              </>
            )}
            <p className="text-gray-600 dark:text-gray-400 mt-1">Connectez-vous à votre espace</p>
          </div>

          {/* Message d'erreur / info */}
          {error && (
            <div className={`mb-4 p-4 border rounded-lg ${getErrorStyles(error.type).container}`}>
              <div className="flex items-start gap-3">
                {(() => { const Icon = getErrorStyles(error.type).icon; return <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />; })()}
                <div className="flex-1">
                  <p className="text-sm font-medium">{error.message}</p>
                  {error.action === 'setup' && (
                    <Link to="/forgot-password" className="inline-flex items-center gap-1 mt-2 text-sm font-semibold underline hover:no-underline">
                      <KeyRound className="w-4 h-4" /> Définir mon mot de passe
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => { 
                    setEmail(e.target.value); 
                    setEmailStatus(null);
                    if (error) setError(null); 
                  }} 
                  onBlur={checkEmailStatus}
                  className={`input pl-10 pr-10 ${
                    emailStatus?.exists && !emailStatus?.canAccess ? 'border-red-300 dark:border-red-700' : 
                    emailStatus?.exists && !emailStatus?.isActivated ? 'border-blue-300 dark:border-blue-700' : ''
                  }`}
                  placeholder={getEmailPlaceholder()} 
                  autoComplete="email" 
                  autoFocus 
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {renderEmailStatus()}
                </div>
              </div>
              {/* Nom de l'utilisateur si trouvé */}
              {emailStatus?.exists && emailStatus?.name && emailStatus?.isActivated && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                  <User className="w-4 h-4" /> Bonjour {emailStatus.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => { 
                    setPassword(e.target.value); 
                    if (error?.type === 'error') setError(null); 
                  }} 
                  className="input pl-10" 
                  placeholder="••••••••" 
                  autoComplete="current-password"
                  disabled={emailStatus?.exists && !emailStatus?.isActivated}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || (emailStatus?.exists && !emailStatus?.isActivated) || (emailStatus?.exists && !emailStatus?.canAccess)} 
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
              style={{ backgroundColor: tenant?.primaryColor }}
            >
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Connexion...</>) : 'Se connecter'}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
            <Link to="/forgot-password" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Mot de passe oublié ?</Link>
            {tenant?.slug && (
              <p className="mt-4 text-xs text-gray-500">Portail réservé aux utilisateurs @{tenant.supportEmail?.split('@')[1] || tenant.slug}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;